import { getFirebaseAnalytics } from "@/config/firebase";
import { logEvent, setUserId, setUserProperties } from "firebase/analytics";

/**
 * 라우트에서 정적 세그먼트로 취급하는 경로 이름 목록.
 * 이 목록에 없는 세그먼트는 동적 ID로 간주하여 `:id`로 마스킹된다.
 */
const KNOWN_SEGMENTS = new Set([
  "centers",
  "instructors",
  "members",
  "journals",
  "settings",
  "settlements",
  "journal",
  "classes",
  "movements",
  "settlement",
  "pending-journals",
  "completed-journals",
  "login",
]);

/**
 * 동적 라우트 파라미터(ID 값)를 `:id`로 치환하여 PII 노출을 방지한다.
 * 예: `/centers/123/members/uid456` → `/centers/:id/members/:id`
 */
export function maskRoutePath(path: string): string {
  return path
    .split("/")
    .map((segment) => (segment === "" || KNOWN_SEGMENTS.has(segment) ? segment : ":id"))
    .join("/");
}

/** GA4 이벤트 이름 상수 (snake_case, GA4 표준 네이밍 준수) */
export const EVENT_NAMES = {
  PAGE_VIEW: "page_view",
  JOURNAL_STARTED: "journal_started",
  JOURNAL_COMPLETED: "journal_completed",
  CLASS_SELECTED: "class_selected",
  SETTLEMENT_VIEWED: "settlement_viewed",
  CENTER_SELECTED: "center_selected",
} as const;

export type EventName = (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES];

/** 커스텀 비즈니스 이벤트 이름 (page_view는 trackPageView로 별도 처리). */
export type BusinessEventName = Exclude<EventName, typeof EVENT_NAMES.PAGE_VIEW>;

/**
 * 페이지 뷰 이벤트를 Firebase Analytics에 기록한다.
 * path는 PII 마스킹 처리 후 전송된다.
 * Analytics가 초기화되지 않은 경우(에뮬레이터 모드, measurementId 미설정) 무시한다.
 */
export function trackPageView(path: string): void {
  const analytics = getFirebaseAnalytics();
  if (!analytics) return;

  logEvent(analytics, EVENT_NAMES.PAGE_VIEW, {
    page_path: maskRoutePath(path),
  });
}

/**
 * 커스텀 비즈니스 이벤트를 Firebase Analytics에 기록한다.
 * Analytics가 초기화되지 않은 경우 무시한다.
 */
export function trackEvent(
  eventName: BusinessEventName,
  params?: Record<string, string | number | boolean>,
): void {
  const analytics = getFirebaseAnalytics();
  if (!analytics) return;

  logEvent(analytics, eventName, params);
}

/**
 * 로그인 시 사용자 ID와 역할 속성을 설정한다.
 * Analytics가 초기화되지 않은 경우 무시한다.
 */
export function setAnalyticsUser(uid: string, role: string): void {
  const analytics = getFirebaseAnalytics();
  if (!analytics) return;

  setUserId(analytics, uid);
  setUserProperties(analytics, { user_role: role });
}

/**
 * 로그아웃 시 사용자 컨텍스트를 초기화한다.
 * 공유 기기에서 사용자 혼용을 방지하기 위해 반드시 호출해야 한다.
 * Analytics가 초기화되지 않은 경우 무시한다.
 */
export function clearAnalyticsUser(): void {
  const analytics = getFirebaseAnalytics();
  if (!analytics) return;

  setUserId(analytics, null);
}

/**
 * 현재 선택된 센터 ID를 GA4 user property로 설정한다.
 * 센터 전환 시 호출하여 이후 이벤트에 center_id가 자동 첨부되도록 한다.
 * Analytics가 초기화되지 않은 경우 무시한다.
 */
export function setAnalyticsCenterProperty(centerId: number): void {
  const analytics = getFirebaseAnalytics();
  if (!analytics) return;

  setUserProperties(analytics, { center_id: String(centerId) });
}
